<template>
  <div class="dashboard-container">
    <!-- Header Section -->
    <DashboardHeader 
      :isLoading="isLoading"
      :startDate="startDate"
      :endDate="endDate"
      :dateRangePresets="dateRangePresets"
      :currentDatePreset="currentDatePreset"
      :lastUpdated="lastUpdated"
      @refresh="refreshData"
      @update-date-range="updateDateRange"
      @set-date-range="setDateRange"
      @apply-date-range="applyDateRange"
    />

    <!-- Loading State -->
    <LoadingState v-if="isLoading" />

    <!-- Error State -->
    <ErrorState v-else-if="error" :error="error" @retry="refreshData" />

    <!-- Main Dashboard Content -->
    <div v-else class="dashboard-content">
      <!-- Key Metrics Section -->
      <section class="metrics-section">
        <div class="metrics-grid">
          <MetricCard
            v-for="metric in keyMetrics"
            :key="metric.id"
            :title="metric.title"
            :value="metric.value"
            :change="metric.change"
            :changeType="metric.changeType"
            :icon="metric.icon"
            :trend="metric.trend"
          />
        </div>
      </section>

      <!-- Charts Section -->
      <ChartsSection 
        :revenueChartData="revenueChartData"
        :revenueChartOptions="revenueChartOptions"
        :activityChartData="activityChartData"
        :activityChartOptions="activityChartOptions"
        v-model:revenueTimeframe="revenueTimeframe"
        v-model:activityPeriod="activityPeriod"
      />
      
      <!-- User Activity & Engagement Section -->
      <UserActivitySection
        :userData="dashboardData.user_activity"
      />
      
      <!-- Top Performing Courses Section -->
      <TopCoursesSection
        :coursesData="dashboardData.top_courses"
      />
      
      <!-- Support Tickets Section -->
      <SupportTicketsSection
        :ticketsData="dashboardData.support_tickets"
      />
      
      <!-- System Performance Section (Admin-only) -->
      <SystemPerformanceSection
        v-if="isAdmin"
        :performanceMetrics="dashboardData.system_performance"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import MetricCard from './dashboard/MetricCard.vue';
import DashboardHeader from './dashboard/DashboardHeader.vue';
import LoadingState from './dashboard/LoadingState.vue';
import ErrorState from './dashboard/ErrorState.vue';
import ChartsSection from './dashboard/ChartsSection.vue';
import UserActivitySection from './dashboard/UserActivitySection.vue';
import TopCoursesSection from './dashboard/TopCoursesSection.vue';
import SupportTicketsSection from './dashboard/SupportTicketsSection.vue';
import SystemPerformanceSection from './dashboard/SystemPerformanceSection.vue';

export default {
  name: 'DashboardView',
  components: {
    MetricCard,
    DashboardHeader,
    LoadingState,
    ErrorState,
    ChartsSection,
    UserActivitySection,
    TopCoursesSection,
    SupportTicketsSection,
    SystemPerformanceSection
  },
  setup() {
    // Core state variables
    const isLoading = ref(true);
    const error = ref(null);
    const dashboardData = ref({});
    const lastUpdated = ref(new Date().toLocaleTimeString());
    const isAdmin = ref(true); // In real app, would check user permissions
    let refreshInterval = null;
    
    // Filter and timeframe selections
    const revenueTimeframe = ref('7d');
    const activityPeriod = ref('daily');
    const engagementPeriod = ref('30d');
    const trendingPeriod = ref('7d');
    const ticketFilter = ref('all');
    const retentionCohort = ref('monthly');
    const predictionTimeframe = ref('30d');
    const funnelPeriod = ref('30d');
    
    // Date range state
    const currentDatePreset = ref('30d');
    const startDate = ref('');
    const endDate = ref('');
    const dateRangePresets = [
      { key: '7d', label: 'Last 7 Days' },
      { key: '30d', label: 'Last 30 Days' },
      { key: '90d', label: 'Last 90 Days' },
      { key: 'ytd', label: 'Year to Date' },
      { key: 'custom', label: 'Custom' }
    ];
    
    // Initialize date range on component creation
    const initializeDateRange = () => {
      const now = new Date();
      const end = new Date(now);
      
      // Set end date to today
      endDate.value = end.toISOString().split('T')[0];
      
      // Set start date based on selected preset
      setDateRange(currentDatePreset.value, false);
    };
    
    // Set date range based on preset
    const setDateRange = (preset, shouldApply = true) => {
      currentDatePreset.value = preset;
      const now = new Date();
      const end = new Date(now);
      let start = new Date(now);
      
      switch (preset) {
        case '7d':
          start.setDate(now.getDate() - 7);
          break;
        case '30d':
          start.setDate(now.getDate() - 30);
          break;
        case '90d':
          start.setDate(now.getDate() - 90);
          break;
        case 'ytd':
          start = new Date(now.getFullYear(), 0, 1); // Jan 1 of current year
          break;
        case 'custom':
          // Don't change dates for custom, just set the preset
          return;
      }
      
      // Update date inputs
      startDate.value = start.toISOString().split('T')[0];
      endDate.value = end.toISOString().split('T')[0];
      
      // Apply date range if requested
      if (shouldApply) {
        applyDateRange();
      }
    };
    
    // Update date range on manual input
    const updateDateRange = (type, value) => {
      if (type === 'start') {
        startDate.value = value;
      } else {
        endDate.value = value;
      }
      // Set preset to custom when manually selecting dates
      currentDatePreset.value = 'custom';
    };
    
    // Apply date range filter to dashboard data
    const applyDateRange = () => {
      fetchDashboardData(startDate.value, endDate.value);
    };

    // Computed properties for metrics
    const keyMetrics = computed(() => [
      {
        id: 'users',
        title: 'Total Users',
        value: dashboardData.value.users?.total || 0,
        change: `+${dashboardData.value.users?.new_signups || 0}`,
        changeType: 'positive',
        icon: 'users',
        trend: 'up'
      },
      {
        id: 'revenue',
        title: 'Revenue',
        value: `$${(dashboardData.value.earnings?.total_revenue || 0).toLocaleString()}`,
        change: `${(dashboardData.value.earnings?.revenue_change_percentage || 0).toFixed(1)}%`,
        changeType: dashboardData.value.earnings?.revenue_change >= 0 ? 'positive' : 'negative',
        icon: 'revenue',
        trend: dashboardData.value.earnings?.revenue_change >= 0 ? 'up' : 'down'
      },
      {
        id: 'courses',
        title: 'Active Courses',
        value: dashboardData.value.courses?.total?.courses || 0,
        change: `${(dashboardData.value.courses?.progress?.average_completion || 0).toFixed(1)}%`,
        changeType: 'info',
        icon: 'courses',
        trend: 'stable'
      },
      {
        id: 'assessments',
        title: 'Assessments',
        value: `${dashboardData.value.assessment?.completed || 0}/${dashboardData.value.assessment?.total || 0}`,
        change: `${(dashboardData.value.assessment?.completion_percentage || 0).toFixed(1)}%`,
        changeType: 'info',
        icon: 'assessments',
        trend: 'stable'
      }
    ]);

    // Chart data and options
    const revenueChartData = computed(() => ({
      labels: dashboardData.value.signups?.trends?.map(t => 
        new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      ) || ['Jan 1', 'Jan 2', 'Jan 3', 'Jan 4', 'Jan 5', 'Jan 6', 'Jan 7'],
      datasets: [{
        label: 'Revenue',
        data: dashboardData.value.signups?.trends?.map(t => t.count * 150) || [1200, 1900, 1500, 2100, 2400, 1800, 2300],
        borderColor: 'rgba(79, 70, 229, 1)',
        backgroundColor: 'rgba(79, 70, 229, 0.1)',
        tension: 0.4,
        fill: true
      }]
    }));

    const activityChartData = computed(() => ({
      labels: ['Daily Active', 'Weekly Active', 'Monthly Active'],
      datasets: [{
        data: [
          dashboardData.value.marketing_metrics?.user_retention?.daily_active || 350,
          dashboardData.value.marketing_metrics?.user_retention?.weekly_active || 1200,
          dashboardData.value.marketing_metrics?.user_retention?.monthly_active || 2800
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.9)',
          'rgba(139, 92, 246, 0.9)',
          'rgba(59, 130, 246, 0.9)'
        ],
        borderWidth: 0
      }]
    }));

    const revenueChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(17, 24, 39, 0.85)',
          titleColor: '#f3f4f6',
          bodyColor: '#d1d5db',
          padding: 12,
          cornerRadius: 6
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    };

    const activityChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        }
      },
      cutout: '65%'
    };
    
    // API Integration
    const fetchDashboardData = async (start = null, end = null) => {
      isLoading.value = true;
      error.value = null;
      
      try {
        // Simulate API call with a delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // In a real implementation, we would use start and end params
        console.log(`Fetching data for range: ${start} to ${end}`);
        
        // Mock data for demonstration
        dashboardData.value = {
          users: {
            total: 12458,
            new_signups: 347
          },
          earnings: {
            total_revenue: 152850,
            revenue_change_percentage: 8.7,
            revenue_change: 1
          },
          courses: {
            total: {
              courses: 248
            },
            progress: {
              average_completion: 72.5
            }
          },
          assessment: {
            completed: 1845,
            total: 2578,
            completion_percentage: 71.6
          },
          signups: {
            trends: [
              { date: '2023-05-01', count: 8 },
              { date: '2023-05-02', count: 12 },
              { date: '2023-05-03', count: 10 },
              { date: '2023-05-04', count: 14 },
              { date: '2023-05-05', count: 16 },
              { date: '2023-05-06', count: 12 },
              { date: '2023-05-07', count: 15 }
            ]
          },
          marketing_metrics: {
            user_retention: {
              daily_active: 350,
              weekly_active: 1200,
              monthly_active: 2800
            }
          },
          // New data for additional components
          user_activity: {
            growth: {
              new_signups: 347,
              signup_trend: 8.5,
              retention_rate: 76,
              retention_trend: 2.3
            },
            engagement: {
              avg_session_time: 27,
              session_trend: 5.2,
              completion_rate: 68,
              completion_trend: -1.5
            },
            devices: [
              { name: 'Desktop', percentage: 58, color: '#4f46e5' },
              { name: 'Mobile', percentage: 32, color: '#10b981' },
              { name: 'Tablet', percentage: 10, color: '#f59e0b' }
            ],
            geoDistribution: [
              { name: 'United States', users: 4235, percentage: 34 },
              { name: 'India', users: 2018, percentage: 16 },
              { name: 'United Kingdom', users: 1427, percentage: 11 },
              { name: 'Canada', users: 1125, percentage: 9 },
              { name: 'Germany', users: 876, percentage: 7 }
            ],
            popularTimes: [
              { time: '6am', value: 250 },
              { time: '9am', value: 680 },
              { time: '12pm', value: 540 },
              { time: '3pm', value: 480 },
              { time: '6pm', value: 780 },
              { time: '9pm', value: 890 },
              { time: '12am', value: 320 }
            ]
          },
          top_courses: [
            {
              id: 1,
              title: 'Machine Learning Fundamentals',
              instructor: 'Dr. Sarah Johnson',
              image: 'https://via.placeholder.com/300x180?text=ML+Course',
              enrollments: 1245,
              completion: 78,
              rating: 4.8,
              badge: { type: 'trending', text: 'Trending' }
            },
            {
              id: 2,
              title: 'Web Development Bootcamp',
              instructor: 'Michael Chen',
              image: 'https://via.placeholder.com/300x180?text=Web+Dev',
              enrollments: 987,
              completion: 65,
              rating: 4.6,
              badge: { type: 'popular', text: 'Popular' }
            },
            {
              id: 3,
              title: 'Python for Data Science',
              instructor: 'Alex Rivera',
              image: 'https://via.placeholder.com/300x180?text=Python',
              enrollments: 1087,
              completion: 72,
              rating: 4.7,
              badge: { type: 'new', text: 'New' }
            },
            {
              id: 4,
              title: 'UX/UI Design Principles',
              instructor: 'Emma Watson',
              image: 'https://via.placeholder.com/300x180?text=UX+Design',
              enrollments: 856,
              completion: 81,
              rating: 4.9,
              badge: null
            }
          ],
          support_tickets: {
            tickets: [
              {
                id: 'TKT-1025',
                user: { name: 'John Smith', email: 'john.smith@example.com' },
                subject: 'Cannot access course materials',
                priority: 'high',
                status: 'open',
                created_at: new Date(Date.now() - 8 * 60 * 60 * 1000)
              },
              {
                id: 'TKT-1024',
                user: { name: 'Sarah Johnson', email: 'sarah.j@example.com' },
                subject: 'Payment processing issue',
                priority: 'high',
                status: 'in_progress',
                created_at: new Date(Date.now() - 12 * 60 * 60 * 1000)
              },
              {
                id: 'TKT-1023',
                user: { name: 'Michael Chen', email: 'mchen@example.com' },
                subject: 'Certificate not generating after completion',
                priority: 'medium',
                status: 'open',
                created_at: new Date(Date.now() - 16 * 60 * 60 * 1000)
              },
              {
                id: 'TKT-1022',
                user: { name: 'Emily Davis', email: 'emily.d@example.com' },
                subject: 'Need to reset password',
                priority: 'low',
                status: 'resolved',
                created_at: new Date(Date.now() - 24 * 60 * 60 * 1000)
              },
              {
                id: 'TKT-1021',
                user: { name: 'Robert Wilson', email: 'rwilson@example.com' },
                subject: 'Video streaming quality issue',
                priority: 'medium',
                status: 'in_progress',
                created_at: new Date(Date.now() - 36 * 60 * 60 * 1000)
              }
            ],
            statistics: {
              total: 27,
              open: 8,
              in_progress: 6,
              resolved: 13,
              average_time: 12 // hours
            }
          },
          system_performance: {
            uptime: {
              value: 29.7 * 24, // days in hours
              percentage: 99.98,
              status: 'healthy',
              history: [100, 100, 100, 100, 100, 80, 100, 100, 100, 100, 100, 100]
            },
            api: {
              average_response: 248,
              change: -12,
              status: 'healthy',
              history: [280, 260, 290, 270, 250, 260, 240, 250, 230, 240, 220, 248],
              threshold: {
                warning: 300,
                critical: 500
              },
              max_value: 500
            },
            errors: {
              rate: 0.42,
              change: -0.18,
              status: 'healthy',
              history: [0.8, 0.7, 0.9, 0.6, 0.5, 0.6, 0.4, 0.5, 0.3, 0.4, 0.5, 0.42],
              threshold: {
                warning: 1.0,
                critical: 2.0
              },
              max_value: 3.0
            },
            database: {
              query_time: 87,
              change: 5,
              status: 'healthy',
              history: [75, 80, 78, 82, 85, 79, 80, 83, 85, 82, 84, 87],
              threshold: {
                warning: 120,
                critical: 200
              },
              max_value: 250
            },
            alerts: [
              {
                severity: 'warning',
                message: 'Increased error rate detected in authentication service',
                timestamp: new Date(Date.now() - 95 * 60 * 1000) // 95 minutes ago
              },
              {
                severity: 'info',
                message: 'Routine database maintenance scheduled for Sunday 02:00 UTC',
                timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000) // 4 hours ago
              },
              {
                severity: 'critical',
                message: 'API server experienced 4 minutes of downtime',
                timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) // 2 days ago
              }
            ]
          }
        };
        
        lastUpdated.value = new Date().toLocaleTimeString();
        
        // In a real implementation, we would make an actual API call:
        /*
        const params = new URLSearchParams();
        if (start) params.append('start_date', start);
        if (end) params.append('end_date', end);
        
        const apiUrl = import.meta.env.VITE_API_URL + '/admin/dashboard/statistics' + 
                      (params.toString() ? '?' + params.toString() : '');
        
        const authToken = localStorage.getItem('auth_token');
        if (!authToken) {
          throw new Error('Authentication required');
        }
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Server error: ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          dashboardData.value = result.data;
          lastUpdated.value = new Date().toLocaleTimeString();
        } else {
          throw new Error(result.message || 'Failed to fetch dashboard data');
        }
        */
        
      } catch (e) {
        console.error('Error fetching dashboard data:', e);
        error.value = e.message || 'An error occurred while fetching dashboard data';
      } finally {
        isLoading.value = false;
      }
    };

    const refreshData = () => {
      fetchDashboardData(startDate.value, endDate.value);
    };

    // Helper function to format dates
    const formatDate = (date) => {
      const now = new Date();
      const diff = now - date;
      
      // Less than 24 hours
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        if (hours < 1) return 'Just now';
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }
      
      // Less than 7 days
      if (diff < 604800000) {
        const days = Math.floor(diff / 86400000);
        return `${days} day${days > 1 ? 's' : ''} ago`;
      }
      
      // Format as date
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Lifecycle hooks
    onMounted(() => {
      initializeDateRange();
      fetchDashboardData(startDate.value, endDate.value);
      
      // Set up auto-refresh interval
      refreshInterval = setInterval(() => {
        fetchDashboardData(startDate.value, endDate.value);
      }, 300000); // Refresh every 5 minutes
    });

    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    });

    // Return all necessary data and functions for the template
    return {
      // Core state
      isLoading,
      error,
      dashboardData,
      lastUpdated,
      isAdmin,
      
      // Date handling
      currentDatePreset,
      startDate,
      endDate,
      dateRangePresets,
      setDateRange,
      updateDateRange,
      applyDateRange,
      
      // Filters and timeframes
      revenueTimeframe,
      activityPeriod,
      engagementPeriod,
      trendingPeriod,
      ticketFilter,
      retentionCohort,
      predictionTimeframe,
      funnelPeriod,
      
      // Data and metrics
      keyMetrics,
      revenueChartData,
      activityChartData,
      revenueChartOptions,
      activityChartOptions,
      
      // Actions
      refreshData,
      
      // Helpers
      formatDate
    };
  }
};
</script>

<style scoped>
/* Only keep base styles here, move specific section styles to their components */
.dashboard-container {
  @apply min-h-screen bg-gray-50 p-6 sm:p-8 lg:p-10;
}

.dashboard-content {
  @apply space-y-6 sm:space-y-8;
}

.metrics-section {
  @apply mb-6 sm:mb-8;
}

.metrics-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6;
}

@media (max-width: 640px) {
  .dashboard-container {
    @apply p-4;
  }
}
</style> 